"use client";

import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { BookOpen, Mic, Radio } from "lucide-react";
import NextLink from "next/link";
import { usePathname } from "next/navigation";
import { Suspense } from "react";
import { useLocale, useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { defaultLocale } from "@/i18n/routing";
import { DiscordInviteLink } from "@/components/community/DiscordInviteLink";

type NavLink = { labelKey: string; href?: string; anchor?: string };

// Problem-focused landing pages that render the full homepage section stack
// (features / pricing / faq), so nav anchors should resolve in-page.
const PROBLEM_LANDING_SLUGS = [
  "save-reading-time",
  "audio-learning",
  "study-with-adhd",
  "repurpose-your-content",
  "screen-free-learning",
  "reading-backlog",
  "learn-on-your-commute",
  "ai-podcast-generator",
] as const;

const navLinks: NavLink[] = [
  { labelKey: "navHowItWorks", anchor: "features" },
  { labelKey: "navVoices", href: "/voices" },
  { labelKey: "navPricing", anchor: "pricing" },
  { labelKey: "navFaq", anchor: "faq" },
  { labelKey: "navContact", href: "/contact" },
];

function NavLinks() {
  const t = useTranslations("header");
  const pathname = usePathname();
  const locale = useLocale();
  // English is unprefixed (`localePrefix: "as-needed"` in i18n/routing.ts).
  const homePath = locale === defaultLocale ? "/" : `/${locale}`;
  // Strip an optional leading locale prefix (e.g. /fr/...) before matching.
  const pathWithoutLocale = (pathname ?? "").replace(/^\/[a-z]{2}(?=\/|$)/, "");
  const isProblemLanding = PROBLEM_LANDING_SLUGS.some(
    (slug) => pathWithoutLocale === `/${slug}`,
  );
  const hasLandingSections =
    pathname === "/" ||
    pathname?.endsWith("-to-podcast") === true ||
    pathname === "/embed-podcast" ||
    isProblemLanding ||
    // Locale-prefixed equivalents
    /^\/[a-z]{2}$/.test(pathname ?? "") ||
    /^\/[a-z]{2}\/embed-podcast$/.test(pathname ?? "") ||
    /^\/[a-z]{2}\/.*-to-podcast$/.test(pathname ?? "");

  const resolveHref = (link: NavLink): string => {
    if (link.href) return link.href;
    if (!link.anchor) return "#";
    return hasLandingSections ? `#${link.anchor}` : `${homePath}#${link.anchor}`;
  };

  return (
    <>
      {navLinks.map((link) =>
        link.href ? (
          <Link
            key={link.labelKey}
            href={link.href}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {t(link.labelKey)}
          </Link>
        ) : (
          <a
            key={link.labelKey}
            href={resolveHref(link)}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {t(link.labelKey)}
          </a>
        ),
      )}
    </>
  );
}

function NavLinksFallback() {
  const t = useTranslations("header");
  const locale = useLocale();
  // English is unprefixed (`localePrefix: "as-needed"` in i18n/routing.ts).
  const homePath = locale === defaultLocale ? "/" : `/${locale}`;
  return (
    <>
      {navLinks.map((link) =>
        link.href ? (
          <Link
            key={link.labelKey}
            href={link.href}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {t(link.labelKey)}
          </Link>
        ) : (
          <a
            key={link.labelKey}
            href={`${homePath}#${link.anchor ?? ""}`}
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {t(link.labelKey)}
          </a>
        ),
      )}
    </>
  );
}

export function Header() {
  const t = useTranslations("header");
  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link
            href="/"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center">
              <Mic className="w-4 h-4 text-accent-foreground" />
            </div>
            <span className="font-bold text-lg">Podcastify</span>
          </Link>

          <nav
            aria-label="Primary"
            className="hidden md:flex items-center gap-8"
          >
            <Suspense fallback={<NavLinksFallback />}>
              <NavLinks />
            </Suspense>
            <NavigationMenu viewport={false}>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-sm text-muted-foreground hover:text-foreground bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent data-[state=open]:text-foreground h-auto px-0 py-0 font-normal">
                    {t("exploreMenu")}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[280px] gap-1 p-2">
                      <li>
                        {/* /{locale}/explore 301s to /explore in proxy.ts, so keep
                            next/link here to avoid a redirect hop from locale pages. */}
                        <NavigationMenuLink asChild>
                          <NextLink
                            href="/explore"
                            className="flex items-start gap-3 rounded-md p-3 hover:bg-accent transition-colors"
                          >
                            <Radio className="mt-0.5 size-4 text-accent-foreground shrink-0" />
                            <div>
                              <p className="text-sm font-medium leading-none mb-1">{t("exploreLabel")}</p>
                              <p className="text-xs text-muted-foreground">{t("exploreDescription")}</p>
                            </div>
                          </NextLink>
                        </NavigationMenuLink>
                      </li>
                      <li>
                        <NavigationMenuLink asChild>
                          <Link
                            href="/blog"
                            className="flex items-start gap-3 rounded-md p-3 hover:bg-accent transition-colors"
                          >
                            <BookOpen className="mt-0.5 size-4 text-accent-foreground shrink-0" />
                            <div>
                              <p className="text-sm font-medium leading-none mb-1">{t("blogLabel")}</p>
                              <p className="text-xs text-muted-foreground">{t("blogDescription")}</p>
                            </div>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
            <Link
              href="/tools"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              {t("navTools")}
            </Link>
          </nav>

          <div className="flex items-center gap-2 md:gap-4">
            <DiscordInviteLink
              source="header"
              iconOnly
              ariaLabel={t("navCommunity")}
              className="hidden md:inline-flex rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
              iconClassName="size-5"
            />
            <div className="flex items-center gap-4">
              {/* prefetch={false}: /login redirects cookie-less visitors to
                  /onboarding via middleware; prefetching it would cache that
                  redirect in the client router cache and later reuse it after
                  the onboarding cookies are set (vercel/next.js#88937),
                  stranding guests on the onboarding page. */}
              <Link href="/login" prefetch={false}>
                <Button className="bg-accent text-black hover:bg-accent/90">{t("getStarted")}</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
